import React from 'react';
import { Banknote, CreditCard } from 'lucide-react';
import { paymentWords } from '../commerce/paymentWords';
import type { CheckoutLanguage } from './checkoutLabels';

// How the order is paid: the cash-on-delivery note every store shows, and beside it, for a store that takes cards,
// a card option that sends the shopper to Stripe's own page. One radio group, so a keyboard moves between them.

/** The two ways an order may be paid at checkout. */
export type CheckoutPayment = 'COD' | 'STRIPE';

type PaymentChoiceProps = {
  takesCards: boolean; value: CheckoutPayment; onChange: (next: CheckoutPayment) => void;
  language: CheckoutLanguage; cashTitle: string; cashText: string;
};

// Draws one way to pay as a row: its icon, its name, and a line under it.
const Option: React.FC<{ icon: React.ReactNode; title: string; text: string; input?: React.ReactNode }> = ({ icon, title, text, input }) => (
  <label className="cod-notice-box" style={{ cursor: input ? 'pointer' : 'default' }}>
    {input}
    <div className="cod-notice-icon">{icon}</div>
    <div>
      <div style={{ fontWeight: 700, color: '#0f172a' }}>{title}</div>
      <div style={{ fontSize: 12 }}>{text}</div>
    </div>
  </label>
);

// Draws how the order is paid: the cash note alone, or a choice between cash and card when the store takes cards.
export const PaymentChoice: React.FC<PaymentChoiceProps> = ({ takesCards, value, onChange, language, cashTitle, cashText }) => {
  if (!takesCards) return <Option icon={<Banknote size={20} />} title={cashTitle} text={cashText} />;
  const words = paymentWords(language);
  // Draws the radio that picks one way to pay.
  const radio = (method: CheckoutPayment) => (
    <input type="radio" name="xshop-payment" value={method} checked={value === method} onChange={() => onChange(method)} />
  );
  return (
    <fieldset className="xe-payment-choice" style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 8 }}>
      <legend style={{ fontWeight: 700, marginBottom: 6 }}>{words.payment}</legend>
      <Option input={radio('COD')} icon={<Banknote size={20} />} title={cashTitle} text={cashText} />
      <Option input={radio('STRIPE')} icon={<CreditCard size={20} />} title={words.card} text={words.cardNote} />
    </fieldset>
  );
};
